import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Zap, Rocket, Shield, ChevronRight, Code, Server, Globe, MousePointer2, Users, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { ShaderAnimation } from "@/components/ui/shader-animation";

// --- CUSTOM CSS ---
const customStyles = `
  @keyframes flicker {
    0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% { opacity: 0.99; }
    20%, 21.999%, 63%, 63.999%, 65%, 69.999% { opacity: 0.4; }
  }
  @keyframes lightning {
    0%, 95% { opacity: 0; }
    96% { opacity: 0.1; } 
    97% { opacity: 0; }
    98% { opacity: 0.2; background-color: rgba(255,255,255,0.2); }
    99% { opacity: 0; }
    100% { opacity: 0; }
  }
  .bg-grid-pattern {
    background-size: 50px 50px;
    background-image: 
      linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
    mask-image: radial-gradient(circle at center, black 40%, transparent 100%);
  }
  .text-glitch {
    animation: flicker 3s infinite;
    text-shadow: 2px 2px 0px rgba(168, 85, 247, 0.4), -2px -2px 0px rgba(34, 211, 238, 0.4);
  }
`;

const Home = () => {
  const { scrollY } = useScroll();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Mouse Tracking Logic
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="min-h-screen bg-slate-950 text-white overflow-x-hidden font-sans selection:bg-cyan-500 selection:text-black cursor-none">
      <style>{customStyles}</style>

      {/* --- 1. GLOBAL MOUSE TRACKER ("The Flashlight") --- */}
      {/* This follows the mouse everywhere */}
      <div 
        className="fixed pointer-events-none z-50 mix-blend-screen"
        style={{ 
            left: mousePosition.x, 
            top: mousePosition.y,
            transform: 'translate(-50%, -50%)'
        }}
      >
        {/* The large glow */}
        <div className="w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[100px]" />
        {/* The center cursor dot */}
        <div className="absolute top-1/2 left-1/2 w-4 h-4 bg-cyan-400 rounded-full blur-[2px] -translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* --- HERO SECTION --- */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        
        {/* BACKGROUND LAYERS */}
        <div className="absolute inset-0 z-0">
            {/* Shader Animation */}
            <ShaderAnimation />
            
            {/* Dark Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-transparent to-slate-950/90 pointer-events-none" />
            
            {/* Lightning Flash Effect */}
            <div className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{ animation: 'lightning 8s infinite' }} />
        </div>

        {/* CONTENT */}
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
          >
            {/* MSME Badge */}
            <div className="inline-flex items-center gap-2 mb-8 px-6 py-2 rounded-full bg-black/40 border border-cyan-500/50 backdrop-blur-md shadow-[0_0_20px_rgba(6,182,212,0.2)]">
              <Zap className="w-4 h-4 text-yellow-400 fill-yellow-400 animate-pulse" />
              <span className="text-sm font-bold tracking-widest text-cyan-400 uppercase">MSME Registered Entity</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tight drop-shadow-2xl">
              Empowering Web with <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 text-glitch">
                THUNDER-SPEED
              </span> <br />
              Solutions
            </h1>

            <p className="text-xl md:text-2xl text-slate-300 mb-10 max-w-2xl mx-auto font-light leading-relaxed">
              Focus Success in Software and Web Development. <br/>
              <span className="text-cyan-400 font-bold">We engineer digital storms.</span>
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 cursor-auto">
              <Link
                to="/contact"
                className="group relative px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-black text-lg rounded-xl shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all hover:scale-105"
              >
                <span className="flex items-center gap-2">
                   GET STARTED <Zap className="w-5 h-5 fill-black" />
                </span>
                <div className="absolute inset-0 bg-white/30 rounded-xl opacity-0 group-hover:opacity-100 animate-ping" />
              </Link>
              
              <Link
                to="/services"
                className="px-8 py-4 border border-white/20 hover:border-cyan-400/50 hover:bg-white/5 text-white font-bold text-lg rounded-xl transition-all backdrop-blur-sm"
              >
                OUR SERVICES
              </Link>
            </div>
          </motion.div>
        </div>

        {/* --- SCROLL DOWN OPTION --- */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ 
            opacity: { delay: 1, duration: 1 },
            y: { repeat: Infinity, duration: 1.5, ease: "easeInOut" } 
          }}
          onClick={() => scrollToSection('features')}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 group cursor-none"
        >
          <div className="flex flex-col items-center gap-2">
            {/* <span className="text-[10px] uppercase tracking-[0.3em] text-cyan-500 opacity-70 group-hover:opacity-100 transition-opacity">
                Scroll to Access
            </span> */}
            {/* <div className="w-6 h-10 border-2 border-cyan-500/50 rounded-full flex items-start justify-center p-2 shadow-[0_0_15px_rgba(6,182,212,0.3)] backdrop-blur-sm">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                className="w-1 h-2 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(6,182,212,0.8)]"
              />
            </div> */}
          </div>
        </motion.button>

      </section>

      {/* --- FEATURES SECTION --- */}
      <section id="features" className="py-24 relative z-10 bg-slate-950 cursor-auto">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              WHY CHOOSE <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">THUNDER FOX?</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              We combine innovation, speed, and reliability to deliver exceptional digital solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Lightning Fast",
                desc: "Optimized performance and rapid development cycles for quick market entry.",
                color: "text-yellow-400",
                bg: "bg-yellow-400/10"
              },
              {
                icon: Shield,
                title: "Trusted & Secure",
                desc: "MSME registered with enterprise-grade security protocols.",
                color: "text-cyan-400",
                bg: "bg-cyan-400/10"
              },
              {
                icon: Rocket,
                title: "Innovation Driven",
                desc: "Cutting-edge technologies and modern frameworks for scalable solutions.",
                color: "text-pink-500",
                bg: "bg-pink-500/10"
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative p-1 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden hover:scale-[1.02] transition-transform duration-300"
              >
                <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-${feature.color.split('-')[1]}-500 to-transparent opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-500`} />
                
                <div className="relative h-full p-8 bg-slate-900/90 backdrop-blur-xl rounded-xl border border-white/5 group-hover:border-white/20 transition-colors">
                  <div className={`inline-flex items-center justify-center w-16 h-16 mb-6 rounded-2xl ${feature.bg} ${feature.color} ring-1 ring-white/10 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-400">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section id="cta" className="py-24 relative overflow-hidden cursor-auto">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-indigo-950/20" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative p-12 md:p-20 bg-slate-900/40 backdrop-blur-xl rounded-3xl border border-cyan-500/30 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-cyan-500/5 animate-pulse" />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                Let's Build Something <br className="md:hidden"/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-red-500">POWERFUL</span> Together ⚡
              </h2>
              
              {/* Counter Section */}
              <CounterSection />
              
              <p className="text-slate-300 text-xl mb-10 max-w-2xl mx-auto">
                Ready to transform your digital presence with lightning-fast solutions?
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-xl rounded-full shadow-[0_0_20px_rgba(8,145,178,0.4)] transition-all hover:scale-105"
              >
                <Rocket className="w-6 h-6" />
                START YOUR PROJECT
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

// --- Counter Component ---
interface CounterProps {
  value: number;
  label: string;
  icon: React.ElementType;
  suffix?: string;
}

const Counter = ({ value, label, icon: Icon, suffix = "" }: CounterProps) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  useEffect(() => {
    if (isInView) {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const increment = value / steps;
      const stepDuration = duration / steps;
      
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, stepDuration);
      
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return (
    <div ref={ref} className="flex flex-col items-center">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-8 h-8 text-cyan-400" />
        <span className="text-5xl md:text-7xl font-black text-white">
          {count}{suffix}
        </span>
      </div>
      <span className="text-slate-400 text-lg font-medium uppercase tracking-wider">{label}</span>
    </div>
  );
};

// --- Counter Section Component ---
const CounterSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <div ref={ref} className="flex flex-wrap justify-center gap-12 md:gap-20 mb-10">
      <Counter value={30} label="Clients" icon={Users} />
      <Counter value={35} label="Projects" icon={Briefcase} />
    </div>
  );
};

export default Home;
